import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Loader2, Plus, Search, Pencil, Trash2, Phone, Mail, MapPin, ChevronLeft, AlertCircle, Users } from 'lucide-react';
import { customerAPI } from '../services/api';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import CustomerModal from './CustomerModal';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "./ui/table";
import {
    Dialog,
    DialogContent,
} from './ui/dialog';
import SuccessScreen from './ui/SuccessScreen';

const CustomerPage = ({ onBack }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const PAGE_SIZE = 10;
    const [error, setError] = useState(null);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [isSuccess, setIsSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [countdown, setCountdown] = useState(3);

    useEffect(() => {
        let timer;
        if (isSuccess && countdown > 0) {
            timer = setInterval(() => setCountdown(c => c - 1), 1000);
        } else if (isSuccess && countdown === 0) {
            setIsSuccess(false);
            setCountdown(3);
        }
        return () => clearInterval(timer);
    }, [isSuccess, countdown]);

    // Zero-Refresh Real-time Registry Synchronizer
    useEffect(() => {
        const socketUrl = '/ws';
        const stompClient = new Client({
            webSocketFactory: () => new SockJS(socketUrl),
            debug: (str) => { /* console.log('Customer Sync: ' + str); */ },
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
        });

        stompClient.onConnect = (frame) => {
            console.log('Customer Registry connected to live-sync: ' + frame);
            stompClient.subscribe('/topic/dashboard', (message) => {
                console.log('Customer Registry broadcast received: ' + message.body);
                if (message.body === 'SYNC') {
                    // Trigger in-place refresh of registry
                    fetchCustomers(page);
                }
            });
        };

        stompClient.onStompError = (frame) => {
            console.error('Customer Sync Broker error: ' + frame.headers['message']);
        };

        stompClient.activate();

        return () => {
            if (stompClient) stompClient.deactivate();
        };
    }, [page]);

    const fetchCustomers = async (pageNo = 0) => {
        setLoading(true);
        try {
            const params = {
                pageNumber: pageNo,
                pageSize: PAGE_SIZE,
                sortBy: 'customerName',
                sortDir: 'asc'
            };

            // If search term exists, filtering should happen ideally on backend.
            // Current backend has search by phone.
            // If the user types a phone number, we can use searchCustomer?
            // Or we rely on the getAllCustomers endpoint if it supports filtering.
            // Looking at `api.js`, `getCustomers` takes params. Backend `CustomerController` `getAllCustomers` takes params.
            // The backend `getAllCustomers` is just a findAll with Pageable. It might not search by name.
            // For now, let's load all and filter client side if backend doesn't support name search in getAll.
            // Wait, previous investigation showed `GET /api/v1/customers` (paginated).

            // Let's rely on standard pagination.
            const response = await customerAPI.getCustomers(params);
            const data = response.data;

            setCustomers(data.content || []);
            setTotalPages(data.totalPages || 0);
            setTotalElements(data.totalElements || 0);
            setPage(pageNo);

        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setLoading(false);
        }
    };

    // Initial Load
    useEffect(() => {
        fetchCustomers(0);
    }, []);

    // Filter Logic (Client-side for now if backend list is small or until backend search is confirmed)
    // Actually, let's just use the list. The backend `searchCustomer` is for phone. 
    // If searchTerm is valid phone, maybe we call that?
    // For now, let's implement client-side filtering on the current page data, or just fetch all.
    // If we have pagination, client side filtering only filters the current page which is bad.
    // Let's assume user wants to browse.

    // Better approach: If searchTerm looks like a phone, call search API. Else load standard list.
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (searchTerm.length >= 4 && !isNaN(searchTerm)) {
                // It's a phone number search
                setLoading(true);
                try {
                    const res = await customerAPI.searchCustomer(searchTerm);
                    // Search returns a single customer object or something?
                    // api.js: return api.get(`/customers/search`, { params: { phone } });
                    // It likely returns a CustomerDTO.
                    if (res.data) {
                        setCustomers([res.data]);
                        setTotalPages(1);
                        setTotalElements(1);
                    } else {
                        setCustomers([]);
                        setTotalPages(0);
                        setTotalElements(0);
                    }
                } catch (err) {
                    setCustomers([]); // Not found
                } finally {
                    setLoading(false);
                }
            } else if (searchTerm === '') {
                fetchCustomers(0);
            }
            // Text search (by name) support isn't explicit in backend list API yet.
        }, 600);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);


    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this customer?")) {
            setError(null);
            try {
                await customerAPI.deleteCustomer(id);
                fetchCustomers(page); // Refresh
                setSuccessMessage('Customer Identity Purged!');
                setIsSuccess(true);
            } catch (error) {
                console.error("Failed to delete customer:", error);
                setError(error.response?.data?.message || error.message || "Failed to delete customer.");
            }
        }
    };

    const handleEdit = (customer) => {
        setSelectedCustomer(customer);
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        setSelectedCustomer(null);
        setIsModalOpen(true);
    };

    const handleSuccess = (action) => {
        fetchCustomers(page);
        setSuccessMessage(action === 'edit' ? 'Client Record Updated!' : 'New Client Onboarded!');
        setIsSuccess(true);
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-10">
            {/* Header & Primary Actions */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-2">
                <div className="flex items-center gap-5">
                    {onBack && (
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={onBack} 
                            className="h-12 w-12 rounded-2xl bg-white shadow-xl shadow-black/5 hover:bg-slate-50 active:scale-95 transition-all"
                        >
                            <ChevronLeft className="h-6 w-6 text-slate-600" />
                        </Button>
                    )}
                    <div className="space-y-1">
                        <h2 className="text-3xl font-black tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-3">
                            <Users className="w-8 h-8 text-violet-600" />
                            Client Registry
                        </h2>
                        <div className="flex items-center gap-2 px-3 py-1 bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800 rounded-full w-fit">
                            <Plus className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
                            <p className="text-[10px] font-black uppercase tracking-wider text-violet-600 dark:text-violet-400">
                                {totalElements} Verified Identities
                            </p>
                        </div>
                    </div>
                </div>
                
                <Button 
                    onClick={handleCreate} 
                    className="h-14 px-8 rounded-2xl bg-black hover:bg-slate-800 text-white font-black uppercase tracking-widest shadow-2xl shadow-black/20 active:scale-95 transition-all text-xs w-full lg:w-auto"
                >
                    <Plus className="mr-3 h-5 w-5" /> Onboard New Client
                </Button>
            </div>

            {/* Global Search Interface */}
            <div className="relative group max-w-2xl">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-slate-400 group-focus-within:text-violet-500 transition-colors" />
                </div>
                <Input
                    type="text"
                    placeholder="Identify client by name, phone fingerprint, or digital signature..."
                    className="h-14 pl-12 pr-4 rounded-2xl border-slate-200 bg-white shadow-xl shadow-black/5 focus:ring-4 focus:ring-violet-500/10 font-bold text-base transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                {loading && (
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                        <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
                    </div>
                )}
            </div>



            {
                error && (
                    <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm">{error}</span>
                    </div>
                )
            }

            <Card className="border-none shadow-none bg-transparent">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex justify-center p-12">
                            <div className="flex flex-col items-center gap-3">
                                <Loader2 className="h-10 w-10 animate-spin text-sky-500" />
                                <p className="text-muted-foreground animate-pulse">Loading customers...</p>
                            </div>
                        </div>
                    ) : customers.length === 0 ? (
                        <div className="text-center py-16 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                            <div className="flex flex-col items-center gap-3 text-muted-foreground">
                                <Search className="h-10 w-10 text-slate-300" />
                                <p>No customers found matching your search.</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Desktop View: Enhanced Table */}
                            <div className="hidden md:block rounded-2xl border border-slate-200 bg-white dark:bg-slate-900 shadow-2xl shadow-black/5 overflow-hidden">
                                <Table>
                                    <TableHeader className="bg-slate-50/50 dark:bg-slate-800/50">
                                        <TableRow className="hover:bg-transparent border-b-slate-200">
                                            <TableHead className="w-[80px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6 pl-6">ID</TableHead>
                                            <TableHead className="w-[200px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Client Identity</TableHead>
                                            <TableHead className="font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Channels</TableHead>
                                            <TableHead className="font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Origin Point</TableHead>
                                            <TableHead className="w-[150px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">GST Protocol</TableHead>
                                            <TableHead className="text-right w-[120px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6 pr-6">Management</TableHead>
                                        </TableRow>
                                    </TableHeader>

                                    <TableBody>
                                        {customers.map((customer, idx) => (
                                            <TableRow key={customer.customerId} className="group border-b-slate-100 hover:bg-violet-50/30 dark:hover:bg-slate-900/40 transition-all duration-300">
                                                <TableCell className="font-mono text-[10px] font-black text-slate-400 group-hover:text-violet-600 transition-colors pl-6">0{page * PAGE_SIZE + idx + 1}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-9 w-9 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center font-black text-xs shadow-sm shadow-violet-500/10">
                                                            {customer.customerName.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="font-black text-slate-900 dark:text-slate-100 group-hover:translate-x-1 transition-transform duration-300">
                                                            {customer.customerName}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-col gap-1">
                                                        <span className="flex items-center gap-2 text-xs font-black text-slate-700 dark:text-slate-300">
                                                            <Phone className="h-3.5 w-3.5 text-violet-500" /> {customer.customerContactNumber}
                                                        </span>
                                                        {customer.customerEmail && (
                                                            <span className="flex items-center gap-2 text-[10px] font-bold text-slate-400 group-hover:text-slate-500 uppercase tracking-tighter">
                                                                <Mail className="h-3.5 w-3.5 text-slate-300" /> {customer.customerEmail}
                                                            </span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-start gap-2 text-xs font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest max-w-[200px]">
                                                        <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-300" />
                                                        <span className="truncate">
                                                            {customer.billingCity}, {customer.billingState}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="font-mono text-[10px] font-black bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg text-slate-400 shadow-sm border border-slate-200/50">
                                                        {customer.customerGSTNumber || 'UNREGISTERED'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="pr-6">
                                                    <div className="flex justify-end gap-2">
                                                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-blue-600 hover:bg-blue-600 hover:text-white shadow-sm hover:shadow-blue-500/20 transition-all active:scale-90" onClick={() => handleEdit(customer)}>
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-rose-600 hover:bg-rose-600 hover:text-white shadow-sm hover:shadow-rose-500/20 transition-all active:scale-90" onClick={() => handleDelete(customer.customerId)}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                    </TableBody>
                                </Table>
                            </div>

                            {/* Mobile View: Cards */}
                            <div className="md:hidden space-y-6">
                                {customers.map((customer) => (
                                    <div key={customer.customerId} className="group relative overflow-hidden bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-xl shadow-black/5 border border-slate-100 dark:border-slate-800 transition-all hover:-translate-y-1">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-violet-50 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none opacity-50"></div>
                                        
                                        <div className="relative flex justify-between items-start mb-6">
                                            <div className="flex items-center gap-4">
                                                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400 font-black text-xl shadow-lg shadow-violet-500/20">
                                                    {customer.customerName.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="space-y-1">
                                                    <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{customer.customerName}</h3>
                                                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                        <Phone className="h-3 w-3 text-violet-500" /> {customer.customerContactNumber}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex gap-2">
                                                <Button variant="outline" className="h-10 w-10 p-0 rounded-xl border-slate-200 bg-white shadow-sm hover:bg-black hover:text-white" onClick={() => handleEdit(customer)}>
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button variant="outline" className="h-10 w-10 p-0 rounded-xl border-rose-100 bg-rose-50 text-rose-600 shadow-sm hover:bg-rose-600 hover:text-white" onClick={() => handleDelete(customer.customerId)}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="relative grid grid-cols-1 gap-4 text-sm border-t border-slate-50 dark:border-slate-800 pt-6">
                                            <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50/80 border border-slate-100">
                                                <div className="space-y-0.5">
                                                    <p className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-400">Digital Identity</p>
                                                    <div className="flex items-center gap-2 font-black text-slate-700">
                                                        <Mail className="h-3.5 w-3.5 text-violet-400" />
                                                        <span className="truncate max-w-[150px]">{customer.customerEmail || 'NO_RECORDS'}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right space-y-0.5">
                                                    <p className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-400">Location</p>
                                                    <div className="flex items-center gap-2 font-black text-slate-700">
                                                        <MapPin className="h-3.5 w-3.5 text-rose-400" />
                                                        <span className="truncate max-w-[100px]">{customer.billingCity || '-'}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {customer.customerGSTNumber && (
                                                <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-black text-white">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">GST Protocol</span>
                                                    <span className="font-mono text-xs font-black tracking-widest">{customer.customerGSTNumber}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                        </>
                    )}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-4 mt-6">
                            <span className="text-xs text-muted-foreground font-medium">
                                Page {page + 1} of {totalPages}
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fetchCustomers(page - 1)}
                                    disabled={page === 0}
                                    className="h-8 text-xs"
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fetchCustomers(page + 1)}
                                    disabled={page >= totalPages - 1}
                                    className="h-8 text-xs"
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            <CustomerModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                customerToEdit={selectedCustomer}
                onSuccess={() => handleSuccess(selectedCustomer ? 'edit' : 'add')}
            />

            <Dialog open={isSuccess} onOpenChange={setIsSuccess}>
                <DialogContent className="max-w-md p-0 rounded-[3rem] border-0 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                    <SuccessScreen 
                        message={successMessage || "Registry Updated!"}
                        subMessage="Identity vault synchronized..."
                        countdown={countdown}
                        onAction={() => setIsSuccess(false)}
                    />
                </DialogContent>
            </Dialog>
        </div >
    );
};

export default CustomerPage;
